import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import Typography from '@mui/material/Typography';
import withStyles from '@mui/styles/withStyles';

import { navigateSignin } from '../actions/stepper-actions';
import { setTestId } from '../util/testUtils';
import { submit } from '../actions/invoice-actions';
import EditPaymentOrder from './edit-po-control';
import status from '../constants/invoice-status';

const styles = (theme) => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
  },
  button: {
    margin: theme.spacing(3, 1, 2),
    width: '98%',
  },
  grid: {
    textAlign: 'left',
  },
  divider: {
    margin: theme.spacing(2, 0, 2),
  },
  bolded: {
    fontWeight: 'bold',
  },
  formLabel: {
    marginBottom: theme.spacing(2),
  },
});

class Invoice extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    invoice: PropTypes.shape({
      balanceDue: PropTypes.number,
      invoiceId: PropTypes.number,
      invoicePayments: PropTypes.array,
      invoiceStatusName: PropTypes.string,
      isEditing: PropTypes.bool,
      lineItemDesc: PropTypes.string,
      poNumber: PropTypes.string,
    }).isRequired,
    onNavigateSignin: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
  };

  // eslint-disable-next-line react/sort-comp
  renderLineItems = (lineItem) => {
    if (lineItem) {
      // Split on newline to make it pretty for the user.
      // If no newline characters, original string is returned in an array
      const parts = lineItem.split('\n');

      return parts.map((line) => (
        <Grid item key={line} xs={12}>
          <Typography gutterBottom>{line}</Typography>
        </Grid>
      ));
    }

    return lineItem;
  };

  renderPartialPayments = (invoicePayments) => {
    const { classes } = this.props;
    let partialPayments = null;

    if (invoicePayments.length) {
      partialPayments = invoicePayments.map((payment) => {
        const displayPayment = payment.amount.toFixed(2);
        const displayDate = moment(payment.paidDate).format('MM/DD/YYYY');
        return (
          <React.Fragment key={payment.invoicePaymentId}>
            <Grid item xs={6}>
              <Typography gutterBottom>{displayDate}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right" gutterBottom>
                $ &nbsp;
                {displayPayment}
              </Typography>
            </Grid>
          </React.Fragment>
        );
      });
    } else {
      return null;
    }

    return (
      <React.Fragment>
        <Grid item xs={12}>
          <Divider className={classes.divider} />
        </Grid>
        <Grid item xs={12}>
          <FormLabel className={classes.formLabel} component="legend">
            Previous Payments
          </FormLabel>
        </Grid>
        <Grid item xs={6}>
          <Typography className={classes.bolded} gutterBottom>
            Date Paid
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography align="right" className={classes.bolded} gutterBottom>
            Amount
          </Typography>
        </Grid>
        {partialPayments}
      </React.Fragment>
    );
  };

  isContinueDisabled = (isEditing) => {
    if (!isEditing) {
      // Buttons don't like getting false for the 'disabled' prop.
      // Here, undefined means 'enabled'
      return undefined;
    }

    return true;
  };

  // Do not display button if invoice has been paid already
  isNextButtonDisplayed = (invoiceStatusName) =>
    invoiceStatusName !== status.PAID;

  renderNotes = (notes) => {
    const { classes } = this.props;

    if (notes) {
      return (
        <React.Fragment>
          <Grid item xs={12}>
            <FormLabel className={classes.formLabel} component="legend">
              Additional Notes
            </FormLabel>
          </Grid>
          <Grid className={classes.divider} item xs={12}>
            {notes}
          </Grid>
        </React.Fragment>
      );
    }

    return null;
  };

  render() {
    const { classes, invoice, onSubmit, onNavigateSignin } = this.props;

    const {
      invoiceId,
      lineItemDesc,
      balanceDue,
      invoicePayments,
      isEditing,
      invoiceStatusName,
    } = invoice;

    // Do not attempt to render until invoice is loaded
    if (!invoiceId) {
      return null;
    }

    const roundedBal = balanceDue.toFixed(2);

    return (
      <div className={classes.paper}>
        <Grid className={classes.grid} container>
          <Grid item xs={12}>
            {this.renderLineItems(lineItemDesc)}
          </Grid>
          <Grid item xs={12}>
            <Divider className={classes.divider} />
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.bolded} gutterBottom>
              Invoice Number
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography align="right" className={classes.bolded} gutterBottom>
              {invoiceId}
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <Typography className={classes.bolded} gutterBottom>
              Purchase Order Number
            </Typography>
          </Grid>
          <Grid item xs={7}>
            <EditPaymentOrder />
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.bolded} gutterBottom>
              Balance Due
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography align="right" className={classes.bolded} gutterBottom>
              $ &nbsp;
              {roundedBal}
            </Typography>
          </Grid>
          {/* {this.renderNotes(additionalNotes)} */}
          {this.renderPartialPayments(invoicePayments)}
          <Grid item xs={6}>
            <Button
              className={classes.button}
              onClick={onNavigateSignin}
              variant="outlined"
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={6}>
            {this.isNextButtonDisplayed(invoiceStatusName) && (
              <Button
                {...setTestId('next')}
                className={classes.button}
                color="primary"
                disabled={this.isContinueDisabled(isEditing)}
                onClick={onSubmit}
                variant="contained"
              >
                Next
              </Button>
            )}

            {!this.isNextButtonDisplayed(invoiceStatusName) && (
              <Button
                className={classes.button}
                color="primary"
                onClick={onNavigateSignin}
                variant="contained"
              >
                Pay another invoice
              </Button>
            )}
          </Grid>
        </Grid>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { invoice } = state;

  return {
    invoice,
    paymentAmount: invoice.paymentAmount,
    paymentAmountError: state.invoice.paymentAmountError,
    paymentType: state.payment.paymentType,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      onSubmit: submit,
      onNavigateSignin: navigateSignin,
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(Invoice));
